import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/Button.tsx'
import { IconPencil, IconPlus, IconSearch, IconTrash, IconCopy, IconLink } from '@tabler/icons-react'
import { Input } from 'react-daisyui'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useClient } from '@/stores/useClient.ts'
import { PortfoliosProvider, usePortfoliosContext } from '@/contexts/portfoliosContext.tsx'
import MyTable, { MyTableColumn } from '@/components/portal/MyTable.tsx'
import { CreatePortfolioModal, useCreatePortfolioModal } from '@/components/portal/CreatePortfolioModal.tsx'
import { DeleteConfirmModal, useDeleteConfirmModal } from '@/components/portal/DeleteConfirmModal.tsx'
import PortalStandardComponent from '@/components/portal/PortalStandardComponent.tsx'
import { ProfPortfolio } from '@/models/profPortfolio.ts'
import TagBadge from '@/components/portal/TagBadge.tsx'

export default function PortfoliosPage() {
  return (
    <PortfoliosProvider>
      <PortfoliosPageContent />
    </PortfoliosProvider>
  )
}

function PortfoliosPageContent() {
  const portfoliosContext = usePortfoliosContext()!
  const { portfolios, totalCount, isLoading, params, setSort, setPage, setSearch, refetch } = portfoliosContext
  const { createPortfolio, deletePortfolio, duplicatePortfolio } = useClient()
  const { modalRef: createModalRef, show: showCreateModal, close: closeCreateModal } = useCreatePortfolioModal()
  const { modalRef: deleteModalRef, show: showDeleteModal, close: closeDeleteModal } = useDeleteConfirmModal()
  const [isCreating, setIsCreating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [portfolioToDelete, setPortfolioToDelete] = useState<ProfPortfolio | null>(null)
  const [searchInput, setSearchInput] = useState(params.search || '')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setSearch(searchInput)
    }, 280)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [searchInput, setSearch])

  const columns: MyTableColumn<ProfPortfolio>[] = [
    {
      key: 'title',
      label: 'Name',
      sortKey: 'title',
      className: 'w-[40%]',
      render: (portfolio) => (
        <Link to={`/portal/portfolios/${portfolio.id}`} className="hover:underline">
          {portfolio.title}
        </Link>
      ),
    },
    {
      key: 'tags',
      label: 'Tags',
      render: (portfolio) => (
        <div className="flex flex-wrap gap-1">
          {portfolio.whitelist_tags.map(t => <TagBadge key={t.id} tag={t} size="sm" />)}
        </div>
      ),
    },
    {
      key: 'created_at',
      label: 'Erstellt',
      sortKey: 'created_at',
      render: (portfolio) => (
        <span className="opacity-50">{new Date(portfolio.created_at).toLocaleDateString('de-DE')}</span>
      ),
    },
  ]

  async function handleCreatePortfolio(title: string) {
    setIsCreating(true)
    try {
      await createPortfolio(title)
      closeCreateModal()
      refetch()
    } catch (e: any) {
      console.error('Error creating portfolio:', e)
      toast.error('Fehler beim Erstellen des Portfolios')
    } finally {
      setIsCreating(false)
    }
  }

  function handleDeleteClick(portfolio: ProfPortfolio) {
    setPortfolioToDelete(portfolio)
    showDeleteModal()
  }

  async function handleDeleteConfirm() {
    if (!portfolioToDelete) return
    setIsDeleting(true)
    try {
      await deletePortfolio(portfolioToDelete.id)
      closeDeleteModal()
      setPortfolioToDelete(null)
      refetch()
    } catch (e: any) {
      console.error('Error deleting portfolio:', e)
      toast.error('Fehler beim Löschen des Portfolios')
    } finally {
      setIsDeleting(false)
    }
  }

  async function handleDuplicate(portfolio: ProfPortfolio) {
    try {
      await duplicatePortfolio(portfolio.id)
      refetch()
      toast('Portfolio dupliziert', { autoClose: 2000 })
    } catch (e: any) {
      console.error('Error duplicating portfolio:', e)
      toast.error('Fehler beim Duplizieren des Portfolios')
    }
  }

  function handleCopyLink(portfolio: ProfPortfolio) {
    const url = `${window.location.origin}/p/${portfolio.id}`
    navigator.clipboard.writeText(url)
    toast('Link kopiert', { autoClose: 2000 })
  }

  const totalPages = Math.ceil(totalCount / params.page_size)

  return (
    <PortalStandardComponent
      title="Portfolios"
      description="Erstelle automatisch Portfolios aus deinen Projekten und teile sie, um für deine Arbeit zu werben."
      noCard
      actions={
        <Button color="accent" onClick={showCreateModal}>
          <IconPlus size={20} />
          Neues Portfolio
        </Button>
      }
    >
      <div className="mb-6 relative max-w-md">
        <IconSearch size={18} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-40" />
        <Input
          className="pl-10 w-full bg-neutral rounded-full"
          placeholder="Portfolio suchen..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
        />
      </div>

      <div className="bg-neutral border border-[var(--portal-border)] p-3 md:p-6">
        <MyTable
          columns={columns}
          data={portfolios}
          currentSort={params.o}
          onSortChange={setSort}
          isLoading={isLoading}
          emptyText="Noch keine Portfolios vorhanden"
          actions={(portfolio) => (
            <div className="flex gap-0 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
              <Link to={`/portal/portfolios/${portfolio.id}`}>
                <Button size="sm" color="ghost" className="px-2 opacity-50 hover:opacity-100" title="Bearbeiten">
                  <IconPencil size={16} />
                </Button>
              </Link>
              <Button size="sm" color="ghost" className="px-2 opacity-50 hover:opacity-100" onClick={() => handleDuplicate(portfolio)} title="Duplizieren">
                <IconCopy size={16} />
              </Button>
              <Button size="sm" color="ghost" className="px-2 opacity-50 hover:opacity-100" onClick={() => handleCopyLink(portfolio)} title="Link kopieren">
                <IconLink size={16} />
              </Button>
              <Button size="sm" color="ghost" className="px-2 opacity-50 hover:opacity-100 hover:text-error" onClick={() => handleDeleteClick(portfolio)} title="Löschen">
                <IconTrash size={16} />
              </Button>
            </div>
          )}
        />
      </div>

      {totalCount > 0 && (
        <p className="text-sm opacity-50 mt-3">{totalCount} {totalCount === 1 ? 'Portfolio' : 'Portfolios'}</p>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button
            size="sm"
            disabled={params.page <= 1}
            onClick={() => setPage(params.page - 1)}
          >
            Zurück
          </Button>
          <span className="flex items-center px-3 text-sm">
            Seite {params.page} von {totalPages}
          </span>
          <Button
            size="sm"
            disabled={params.page >= totalPages}
            onClick={() => setPage(params.page + 1)}
          >
            Weiter
          </Button>
        </div>
      )}

      <CreatePortfolioModal
        modalRef={createModalRef}
        onConfirm={handleCreatePortfolio}
        isLoading={isCreating}
      />

      <DeleteConfirmModal
        modalRef={deleteModalRef}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
        title="Portfolio löschen"
        message={`Möchtest du das Portfolio „${portfolioToDelete?.title}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
      />
    </PortalStandardComponent>
  )
}
